import type {SyntheticEvent} from 'react';
import type {NativeSyntheticEvent} from 'react-native';

import type {Nullable} from './utility-types';

import {platform} from './platform';

/**
 * Event action, that contains  system data
 * and platform specific event data.
 */
export type EventPayloadAction<P, E> = PayloadAction<P> & {event: E};

/**
 * Common event data structure for standardized event exchange between
 * system components.
 */
export type NormalizedEvent<
  DOMEvent = SyntheticEvent,
  RNEvent = NativeSyntheticEvent<any>,
> = {
  /**
   * DOM specific SyntheticEvent.
   */
  DOMEvent: Nullable<DOMEvent>;
  /**
   * Actual happened SyntheticEvent.
   */
  originalEvent: DOMEvent | RNEvent;
  /**
   * RN specific SyntheticEvent.
   */
  RNEvent: Nullable<RNEvent>;
};

/**
 * So named 'pure' event action, that contains only system data,
 * without any platform specific event data.
 */
export type PayloadAction<P> = {payload: Nullable<P>};

/**
 * Type of callback mapper to cast platform specific event data
 * to a utils form P
 */
type NormalizeEventMapper<PlatformEvent, Payload> = (
  event: PlatformEvent,
) => Payload;

/**
 * Utility for creating standardized objects for data exchange between
 * different components of the system.
 * @param payload - particular data required by external handler's contract.
 * @example
 * const externalHandler:(action:Action)=>any = (action)=>return action.payload.value
 * const innerHandler = () => {
 *   const someData = {value:1}
 *   const action = actionCreator(someData);
 *   externalHandler(action)
 * }
 */
export const payloadActionCreator = <Payload>(
  payload: Nullable<Payload> = null,
): PayloadAction<Payload> => {
  return {payload};
};

/**
 * Utility for creating standardized objects for data exchange between
 * different components of the system.
 * @param event - normalized synthetic event ({@see normalizeEvent}).
 * @param payload - particular data required by external handler's contract.
 * @example
 * const externalEventHandler:(action:EventPayloadAction)=>any = (action)=>return action.payload.value
 * const innerEventHandler = (event:NormalizedEvent) => {
 *   const someData = {value:1}
 *   const action = eventActionCreator(someData, event);
 *   externalEventHandler(action)
 * }
 */
export const eventPayloadActionCreator = <
  P,
  E extends NormalizedEvent = NormalizedEvent,
>(
  payload: Nullable<P>,
  event: E,
): EventPayloadAction<P, E> => {
  return {event, payload};
};

// TODO: doc all use cases: 1) no payload, 2) custom payload
/**
 * Platform specific event normalizer factory.
 * @param domMapper - from DOM event to the Payload.
 * @param rnMapper - from RN event to the Payload.
 * @example
 * const eventNormalizer = crateEventNormalizer<MouseEvent,NativeTouchEvent, {text:string}>(
 * event => ({
 *    text: event.currentTarget.value,
 * }),
 * event => ({
 *     text: event.nativeEvent.text,
 * }),
 * );
 *
 * clickHandler = event => {
 *     const normalized = eventNormalizer(event)
 * }
 */
export function createEventNormalizer<
  DOMEvent extends SyntheticEvent,
  RNEvent extends NativeSyntheticEvent<any>,
  Payload,
>(
  domMapper?: NormalizeEventMapper<DOMEvent, Payload>,
  rnMapper?: NormalizeEventMapper<RNEvent, Payload>,
) {
  return function normalizer(
    event: DOMEvent | RNEvent,
    /**
     * In case you dont retrieve payload out of the event,
     * but build it outside of normalizer.
     */
    customPayload?: Payload,
  ): EventPayloadAction<Payload, NormalizedEvent<DOMEvent, RNEvent>> {
    const normalized = applyEventNormalization<DOMEvent, RNEvent>(event);
    const payload = buildEventPayload(event, {
      customPayload,
      domMapper,
      rnMapper,
    });

    return eventPayloadActionCreator<Payload, typeof normalized>(
      payload,
      normalized,
    );
  };
}

function applyEventNormalization<DOMEvent, RNEvent>(event: DOMEvent | RNEvent) {
  const normalized: NormalizedEvent<DOMEvent, RNEvent> = {
    DOMEvent: null,
    originalEvent: {...event},
    RNEvent: null,
  };

  if (platform.isNative) {
    normalized.RNEvent = event as RNEvent;
  } else {
    normalized.DOMEvent = event as DOMEvent;
  }

  return normalized;
}

function buildEventPayload<DOMEvent, RNEvent, Payload>(
  event: DOMEvent | RNEvent,
  {
    customPayload,
    domMapper,
    rnMapper,
  }: {
    customPayload?: Payload;
    domMapper?: NormalizeEventMapper<DOMEvent, Payload>;
    rnMapper?: NormalizeEventMapper<RNEvent, Payload>;
  },
): Nullable<Payload> {
  let payload: Nullable<Payload> = null;

  if (customPayload) {
    payload = {...customPayload};
  } else {
    if (platform.isNative) {
      payload = rnMapper ? rnMapper(event as RNEvent) : payload;
    } else {
      payload = domMapper ? domMapper && domMapper(event as DOMEvent) : payload;
    }
  }

  return payload;
}
